import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { profileService } from '../services';
import { connectionsService } from '../services';

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [viewingUserData, setViewingUserData] = useState(null);
  const [viewingUserId, setViewingUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ connections: 0, followers: 0, following: 0 });
  const [viewingUserStats, setViewingUserStats] = useState({ connections: 0, followers: 0, following: 0 });

  // Fetch current user's profile
  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;

    setLoading(true);
    try {
      const data = await profileService.getMyProfile();
      setProfile(data);
      setProfileData(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;

    try {
      const statsData = await connectionsService.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [isAuthenticated, user]);

  // Fetch user stats by ID
  const fetchUserStats = useCallback(async (userId) => {
    if (!userId) return;

    try {
      const statsData = await connectionsService.getUserStats(userId);
      setViewingUserStats(statsData);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  }, []);

  // Fetch profile on mount and when auth changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchProfile();
      fetchStats();
    }
  }, [isAuthenticated, user, fetchProfile, fetchStats]);

  const clearViewingUser = () => {
    setViewingUserData(null);
    setViewingUserId(null);
    setViewingUserStats({ connections: 0, followers: 0, following: 0 });
  };

  const value = {
    profile,
    setProfile,
    profileData,
    setProfileData,
    viewingUserData,
    setViewingUserData,
    viewingUserId,
    setViewingUserId,
    stats,
    setStats,
    viewingUserStats,
    setViewingUserStats,
    clearViewingUser,
    fetchProfile,
    fetchStats,
    fetchUserStats,
    loading,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};

export default ProfileContext;
